// watch/EmbedDialog.tsx — ⋯ Embed video (1.27.60): the <iframe> code for another site, with an
// optional start time. The embed plays what a signed-out visitor could watch (see
// embed/EmbedPlayerPage.tsx); anything else shows a link back to TruXSocial.
import { useState } from 'react';
import styled from 'styled-components';
import { Button, Dialog, useToast } from '@/ui';
import { formatClock } from '../../player/chapters';
import { embedCode } from './embedCode';

const Body = styled.div`
  display: grid;
  gap: 12px;
  width: 100%;
  min-width: 0;
  textarea {
    width: 100%;
    min-height: 110px;
    padding: 10px 12px;
    border-radius: ${({ theme }) => theme.radius.sm};
    border: 1px solid ${({ theme }) => theme.color.glassBorder};
    background: ${({ theme }) => theme.color.surface};
    color: ${({ theme }) => theme.color.textPrimary};
    font: 500 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.mono};
    resize: none;
  }
  label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
    color: ${({ theme }) => theme.color.textPrimary};
    font-variant-numeric: tabular-nums;
  }
  p {
    margin: 0;
    font: 400 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export function EmbedDialog({
  postId,
  title,
  startSec,
  onClose,
}: {
  postId: string;
  title: string;
  /** Where the player was when the dialog opened. */
  startSec: number;
  onClose: () => void;
}) {
  const toast = useToast();
  const [atTime, setAtTime] = useState(false);
  const code = embedCode(postId, title, atTime ? startSec : undefined);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      toast('Embed code copied');
    } catch {
      toast('Couldn’t copy. Select the code and copy it yourself');
    }
  }

  return (
    <Dialog open onClose={onClose} title="Embed video">
      <Body>
        <textarea
          aria-label="Embed code"
          readOnly
          value={code}
          onFocus={(e) => e.currentTarget.select()}
        />
        <Row>
          <label>
            <input type="checkbox" checked={atTime} onChange={(e) => setAtTime(e.target.checked)} />
            Start at {formatClock(startSec)}
          </label>
          <Button onClick={() => void copy()}>Copy</Button>
        </Row>
        <p>
          The embedded player shows the video only to people who could watch it without signing in;
          everyone else gets a link to watch it on TruXSocial.
        </p>
      </Body>
    </Dialog>
  );
}
